import { RefObject, useEffect, useRef } from 'react';

type Handle = Readonly<{ id: number; stopPropagation: () => boolean }>;

/**
 * Keeps track of all registered useOnClickOutside consumers and allows
 * handlers to prevent clicks from propagating to previously registered
 * consumers.
 */
const handleStore = { handles: [] as Handle[], nextId: 0 };

/**
 * Returns true if a later registered handle is preventing clicks from
 * propagating.
 */
const isHandleStopped = (handle: Handle) => {
    const handleIndex = handleStore.handles.findIndex(({ id }) => id === handle.id);
    return (
        handleIndex > -1 &&
        handleStore.handles.some(
            (storeHandle, index) => index > handleIndex && storeHandle.stopPropagation(),
        )
    );
};

/**
 * An effect which runs some side-effect when the user interacts outside of a
 * predefined list of elements, either by pointer (pointerdown) or by moving
 * focus (e.g. keyboard activation that opens a modal, or tabbing away).
 *
 * Example: Close modal windows and popups when someone "clicks off" of them or
 * keyboard-navigates away from them.
 *
 * @param refs The element or array of elements that are "safe"
 * to interact with. Interacting with these elements or their descendants in the
 * DOM will not trigger the event handler.
 * @param handler The event handler
 * that will be triggered if one of the elements not in the "safe" list is
 * interacted with.
 * @param enabled Enables or disables hook (defaults to `true`).
 */
export function useOnClickOutside(
    refs: RefObject<Element> | RefObject<Element>[],
    handler: (event: PointerEvent | FocusEvent) => void,
    enabled = true,
    stopPropagation = false,
) {
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const refList = useRef(refs);
    refList.current = refs;

    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    const stopPropagationRef = useRef(stopPropagation);
    stopPropagationRef.current = stopPropagation;

    // Using refs so that event listeners only need to be added/removed once.
    useEffect(() => {
        const hookHandle: Handle = {
            // not the handle count, which repeats live ids once a handle is removed
            id: handleStore.nextId,
            stopPropagation: () => stopPropagationRef.current,
        };
        handleStore.nextId += 1;

        const isOutside = (target: EventTarget | null) => {
            const refsList = Array.isArray(refList.current)
                ? refList.current
                : [refList.current];

            return (
                enabledRef.current &&
                target instanceof Node &&
                !isHandleStopped(hookHandle) &&
                !refsList.some((ref) => ref.current?.contains(target) ?? false)
            );
        };

        const pointerListener = (event: PointerEvent) => {
            if (isOutside(event.target)) {
                handlerRef.current(event);
            }
        };

        // Track the last focus target so we can detect involuntary focus
        // changes (e.g. element became disabled or was removed from the DOM).
        // (relatedTarget seemed unreliable)
        let lastFocused: Element | null = document.activeElement;

        // On focusin, focus may still be moving (through focus-trap guards or
        // onto an autofocused input). Wait for it to settle with setTimeout,
        // then close only if it landed outside. The delay also lets focus-lock
        // effects run, so a trapped modal keeps focus inside and stays open.
        let pendingFocusCheck = 0;
        const focusListener = (event: FocusEvent) => {
            window.clearTimeout(pendingFocusCheck);
            const prevFocused = lastFocused;
            lastFocused = document.activeElement;
            pendingFocusCheck = window.setTimeout(() => {
                // Switching to another window or tab fires a focus change here;
                // without this guard the popup would close while the user is away.
                if (!document.hasFocus()) {
                    return;
                }
                const active = document.activeElement;
                if (active && active !== document.body && isOutside(active)) {
                    if (prevFocused instanceof HTMLElement) {
                        // the previously focused element was removed from the
                        // DOM, so this is not a reliable indicator of user
                        // action
                        if (!prevFocused.isConnected) {
                            return;
                        }
                        // focus was already outside the popup, so this is not a
                        // reliable signal that we should close the popup
                        if (isOutside(prevFocused)) {
                            return;
                        }
                        // the previously focused element may have lost focus
                        // because it became disabled, so this is not a reliable
                        // indicator of a user's intent to close the popup
                        if (!isOutside(prevFocused) && prevFocused.matches(':disabled')) {
                            return;
                        }
                    }
                    handlerRef.current(event);
                }
            });
        };

        document.addEventListener('pointerdown', pointerListener);
        document.addEventListener('focusin', focusListener);
        handleStore.handles.push(hookHandle);

        return () => {
            window.clearTimeout(pendingFocusCheck);
            document.removeEventListener('pointerdown', pointerListener);
            document.removeEventListener('focusin', focusListener);
            handleStore.handles = handleStore.handles.filter(
                (handle) => handle !== hookHandle,
            );
        };
    }, []);
}
